import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import './RightBar.css';
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Drawer,
  MenuItem,
  Menu
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getEvidenceCameras } from '../../../../api/FM/videoPageApis';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CloseOutlined } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Switch from '@mui/material/Switch';
import { MdCameraAlt } from 'react-icons/md';
import EvidenceList from '../evidencePage/evidenceList';
import CameraThumbnail from '../../../../assets/images/404 Error-bro.svg';

const CameraCard = ({ camera, onSelect, isSelected, showEvidenceList }) => {
  const dispatch = useDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const videoPlaying = useSelector((state) => state.cameraReducer.videoPlaying);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteCamera = async () => {
    try {
      setIsDeleting(true);
      await deleteCamera(camera._id);
      dispatch({ type: 'CAMERA_ADDED' });
      dispatch({ type: 'VIDEO_PLAYING', videoPlaying: false });
      setSnackbar({
        open: true,
        message: 'Camera deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to delete camera',
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (videoPlaying) {
      dispatch({ type: 'VIDEO_PLAYING', videoPlaying: false });
    }
    setIsDeleteModalOpen(true);
  };

  const handleToggleCamera = (cameraId) => {
    // Implement your camera toggle logic here
    console.log('Toggle camera', cameraId);
  };

  const handleCameraClick = () => {
    if (!camera.active) {
      return;
    }

    console.log('Camera clicked:', camera);

    // Always update camera data and trigger onSelect
    dispatch({ type: 'CAMERA_DATA', cameraData: camera });
    if (onSelect) {
      onSelect(camera);
    }
  };

  return (
    <div
      className={`flex bg-white rounded-lg shadow-lg text-xs p-4 relative hover:shadow-xl transition-all ${
        isSelected && !showEvidenceList ? 'border-2 border-blue-500 bg-blue-50' : ''
      } ${!camera.active ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
      onClick={handleCameraClick}
    >
      {/* Left Side: Camera Thumbnail */}
      <div className="flex w-1/3 items-center justify-center bg-gray-200 rounded-lg overflow-hidden">
        {camera?.thumbnail ? (
          <img src={CameraThumbnail} alt="Camera Thumbnail" className="w-full h-full object-cover" />
        ) : (
          <MdCameraAlt className="text-gray-500 text-4xl" />
        )}
      </div>

      {/* Right Side: Camera Info */}
      <div className="flex flex-col flex-grow px-4">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-2xl font-semibold">{camera.device_name}</h2>
          <Switch
            checked={camera.active}
            onChange={(e) => {
              e.stopPropagation();
              handleToggleCamera(camera._id);
            }}
            color="success"
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-2">
          {Object.keys(camera.zones || {})
            .slice(0, 2)
            .map((zoneName) => (
              <span key={zoneName} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                {zoneName}
              </span>
            ))}
          {Object.keys(camera.zones || {}).length > 2 && <span className="px-3 py-1 rounded-full text-xs">...</span>}
        </div>
      </div>

      {/* Actions Menu */}
      <div className="absolute top-2 right-2">
        <IconButton size="small" onClick={handleMenuClick}>
          <MoreVertIcon />
        </IconButton>
      </div>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => console.log('View details')}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteModalOpen} onClose={() => !isDeleting && setIsDeleteModalOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this camera? This will also delete all associated zones.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalOpen(false)} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteCamera} color="error" disabled={isDeleting} startIcon={isDeleting && <CircularProgress size={20} />}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const RightBar = () => {
  const dispatch = useDispatch();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showEvidenceList, setShowEvidenceList] = useState(false);
  const cameraAdded = useSelector((state) => state.cameraReducer.cameraAdded);
  const [error, setError] = useState(null);
  const selectedCamera = useSelector((state) => state.cameraReducer.cameraData);
  const selectedDate = useSelector((state) => state.customization.currentDate);

  const fetchCameras = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEvidenceCameras();
      console.log('Fetched cameras response:', response);
      const camerasArray = response?.cameras || [];

      // Add rodent camera when source is dombivli AND date is July 20th
      const source_id = JSON.parse(localStorage.getItem('source'));
      const currentDate = new Date(selectedDate);
      const isJuly20 = currentDate.getMonth() === 6 && currentDate.getDate() === 20; // Month is 0-indexed

      if (source_id === 'dombivli' && isJuly20) {
        const rodentCamera = {
          _id: 'rodent_camera_001',
          device_name: 'Rodent',
          active: true,
          zones: { 'Rodent Zone': true },
          thumbnail: null
        };

        // Use spread operator to add rodent camera
        const updatedCameras = [...camerasArray, rodentCamera];
        setCameras(updatedCameras);
      } else {
        setCameras(camerasArray);
      }

      console.log('Processed cameras array:', camerasArray);
    } catch (err) {
      console.error('Error fetching cameras:', err);
      setError(err.message || 'Failed to fetch cameras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('cameraAdded state changed:', cameraAdded);
    fetchCameras();
    // Reset cameraAdded flag after fetching
    if (cameraAdded) {
      dispatch({ type: 'RESET_CAMERA_ADDED' });
    }
  }, [cameraAdded, dispatch, selectedDate]);

  // Handle camera selection and evidence list display
  const handleCameraSelect = (camera) => {
    if (selectedDate && camera?.device_name) {
      setShowEvidenceList(true);
    }
  };

  // Show evidence list when both date and camera are selected
  useEffect(() => {
    console.log('selected camera:', selectedCamera);
    if (selectedDate && selectedCamera?.device_name) {
      setShowEvidenceList(true);
    } else {
      setShowEvidenceList(false);
    }
  }, [selectedDate, selectedCamera]);

  // Reset camera selection when date changes
  useEffect(() => {
    if (selectedDate) {
      dispatch({ type: 'CAMERA_DATA', cameraData: {} }); // Reset camera selection
      setShowEvidenceList(false); // Hide evidence list until camera is selected
    }
  }, [selectedDate, dispatch]);

  const handleBackToDevices = () => {
    setShowEvidenceList(false);
    // Optionally reset camera selection when going back
    // dispatch({ type: 'CAMERA_DATA', cameraData: {} });
  };

  // Check if a camera is currently selected
  const isCameraSelected = (camera) => {
    return selectedCamera && selectedCamera._id === camera._id;
  };

  return (
    <>
      <div className="relative lg:mt-0-mt-[200%]">
        <IconButton className="absolute top-2 right-2 z-50 md:hidden" onClick={() => setIsDrawerOpen(true)}>
          <ArrowForwardIcon fontSize="small" style={{ color: 'black' }} />
        </IconButton>
      </div>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} className="md:hidden">
        <div className="w-80 p-4 bg-slate-100 border border-gray-300 h-full overflow-y-auto rounded-lg">
          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <CloseOutlined fontSize="small" style={{ color: 'black' }} />
          </IconButton>
          <div className="p-4">
            {showEvidenceList ? (
              <>
                <div className="flex items-center mb-4">
                  <IconButton onClick={handleBackToDevices} size="small" className="mr-2">
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="h5" className="font-medium">
                    Evidence - {selectedCamera?.device_name}
                  </Typography>
                </div>
                {selectedDate && selectedCamera?.device_name && <EvidenceList date={selectedDate} device={selectedCamera} />}
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <Typography variant="h4" className="font-medium">
                    Cameras
                  </Typography>
                </div>

                <div className="space-y-4">
                  {loading ? (
                    <div className="flex justify-center items-center h-full">
                      <CircularProgress />
                    </div>
                  ) : error ? (
                    <div className="flex justify-center items-center h-full">
                      <Typography variant="body1" color="error">
                        {error}
                      </Typography>
                    </div>
                  ) : cameras.length === 0 ? (
                    <div className="flex justify-center items-center h-full">
                      <Typography variant="body1" color="textSecondary">
                        No cameras found
                      </Typography>
                    </div>
                  ) : (
                    cameras
                      .slice()
                      .sort((a, b) => (b.active === a.active ? 0 : b.active ? 1 : -1))
                      .map((camera) => (
                        <CameraCard
                          key={camera._id}
                          camera={camera}
                          onSelect={handleCameraSelect}
                          isSelected={isCameraSelected(camera)}
                          showEvidenceList={showEvidenceList}
                        />
                      ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Drawer>

      {/* Desktop View */}
      <div className="hidden md:block bg-slate-100 border border-gray-300 h-full w-full overflow-y-auto rounded-lg">
        <div className="p-4">
          {showEvidenceList ? (
            <>
              <div className="flex items-center mb-4">
                <IconButton onClick={handleBackToDevices} size="small" className="mr-2">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" className="font-medium">
                  Evidence - {selectedCamera?.device_name}
                </Typography>
              </div>
              {selectedDate && selectedCamera?.device_name && <EvidenceList date={selectedDate} device={selectedCamera} />}
            </>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <Typography variant="h5" className="font-medium">
                  Cameras
                </Typography>
              </div>

              <div className="space-y-4">
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    <CircularProgress />
                  </div>
                ) : error ? (
                  <div className="flex justify-center items-center h-full">
                    <Typography variant="body1" color="error">
                      {error}
                    </Typography>
                  </div>
                ) : cameras.length === 0 ? (
                  <div className="flex justify-center items-center h-full">
                    <Typography variant="body1" color="textSecondary">
                      No cameras found
                    </Typography>
                  </div>
                ) : (
                  cameras
                    .slice()
                    .sort((a, b) => (b.active === a.active ? 0 : b.active ? 1 : -1))
                    .map((camera) => (
                      <CameraCard
                        key={camera._id}
                        camera={camera}
                        onSelect={handleCameraSelect}
                        isSelected={isCameraSelected(camera)}
                        showEvidenceList={showEvidenceList}
                      />
                    ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RightBar;
